import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Newspaper, Clock, TrendingUp, Plus, Radio, Landmark, ExternalLink, Loader2, PenLine } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const LiveArticleCard = ({ article, index }) => (
  <a href={article.url} target="_blank" rel="noopener noreferrer"
    className="block bg-[#111111] border border-white/10 rounded-lg p-5 hover:border-[#007AFF]/50 hover:bg-white/[0.02] transition-all group"
    data-testid={`live-article-${index}`}>
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <span className="bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/30 text-[11px] px-2 py-0.5 rounded-full font-medium">
        {article.source}
      </span>
      {article.institutional && (
        <span className="bg-[#C5A059]/15 text-[#C5A059] border border-[#C5A059]/30 text-[11px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
          <Landmark size={10} /> Institutional
        </span>
      )}
      <span className="text-gray-600 text-xs ml-auto inline-flex items-center gap-1">
        <Clock size={11} /> {timeAgo(article.published_at)}
      </span>
    </div>
    <h3 className="font-manrope font-semibold text-white text-base mb-1.5 group-hover:text-[#007AFF] transition-colors leading-snug">
      {article.title}
    </h3>
    {article.summary && (
      <p className="text-gray-400 text-sm line-clamp-2 mb-2">{article.summary}</p>
    )}
    <span className="text-xs text-gray-600 group-hover:text-[#007AFF] transition-colors inline-flex items-center gap-1">
      Read at source <ExternalLink size={11} />
    </span>
  </a>
);

const NewsPage = () => {
  const [activeTab, setActiveTab] = useState('live');
  const [liveArticles, setLiveArticles] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const categories = ['Crypto', 'Stocks', 'Business', 'Macro', 'Regulation', 'ETFs', 'Institutional'];

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (activeTab === 'live' || activeTab === 'institutional') {
      fetchLive(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchLive = async (tab) => {
    setLiveLoading(true);
    try {
      const endpoint = tab === 'institutional' ? 'institutional' : 'latest';
      const res = await axios.get(`${API}/live-news/${endpoint}`);
      setLiveArticles(res.data.articles);
    } catch (error) {
      console.error('Error fetching live news:', error);
      setLiveArticles([]);
    } finally {
      setLiveLoading(false);
    }
  };

  const fetchNews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      const response = await axios.get(`${API}/news/posts`, { params });
      const allNews = response.data;
      const featuredPost = allNews.find(post => post.featured);
      setFeatured(featuredPost || null);
      setNews(featuredPost ? allNews.filter(post => !post.featured) : allNews);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = news.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLive = liveArticles.filter(
    (a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.summary || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'live', label: 'Latest News', icon: Radio },
    { id: 'institutional', label: 'Institutional Investments', icon: Landmark },
    { id: 'analysis', label: 'DGG Analysis', icon: PenLine },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex justify-between items-start">
            <div>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="news-title">
                Market <span className="text-[#007AFF]">Intelligence</span>
              </h1>
              <p className="text-gray-300 text-lg">
                Live headlines, institutional money flows, and DigitalGoldGang analysis.
              </p>
            </div>
            {user && user.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin')}
                className="bg-[#007AFF] text-white hover:bg-[#2E90FA] rounded-sm px-6 py-3 font-semibold shadow-[0_0_15px_rgba(0,122,255,0.3)] hover:shadow-[0_0_25px_rgba(0,122,255,0.5)] transition-all"
                data-testid="create-news-button"
              >
                <Plus size={20} className="mr-2" />
                Create News
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-0">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  data-testid={`news-tab-${tab.id}`}
                  className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                    activeTab === tab.id
                      ? 'border-[#007AFF] text-[#007AFF]'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}>
                  <Icon size={15} /> {tab.label}
                  {tab.id === 'live' && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder={activeTab === 'analysis' ? 'Search DGG analysis...' : 'Search headlines...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-sm h-12"
              data-testid="search-input"
            />
          </div>

          {/* LIVE / INSTITUTIONAL TABS */}
          {(activeTab === 'live' || activeTab === 'institutional') && (
            <div data-testid="live-news-section">
              {activeTab === 'institutional' && (
                <div className="flex items-start gap-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-lg px-4 py-3 mb-6">
                  <Landmark size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-400">
                    Headlines filtered for institutional activity: funding rounds, ETF flows, treasury purchases,
                    venture investments and new project backing — sourced live from CoinDesk, CoinTelegraph, Decrypt and CryptoPanic.
                  </p>
                </div>
              )}
              {liveLoading ? (
                <div className="flex items-center justify-center py-16" data-testid="live-news-loading">
                  <Loader2 size={28} className="animate-spin text-[#007AFF]" />
                  <span className="ml-3 text-gray-400">Fetching latest headlines...</span>
                </div>
              ) : filteredLive.length === 0 ? (
                <div className="text-center py-16">
                  <Newspaper className="mx-auto text-gray-600 mb-4" size={48} />
                  <p className="text-gray-400">No headlines found right now. Try again shortly.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredLive.map((a, i) => <LiveArticleCard key={a.id} article={a} index={i} />)}
                </div>
              )}
            </div>
          )}

          {/* DGG ANALYSIS TAB (curated posts) */}
          {activeTab === 'analysis' && (
            <div data-testid="dgg-analysis-section">
              {/* Featured */}
              {featured && !searchTerm && (
                <Link
                  to={`/news/${featured.news_id}`}
                  className="block mb-10 bg-[#111111] border border-white/10 rounded-lg overflow-hidden hover:border-[#007AFF]/50 hover:shadow-lg transition-all duration-300 group"
                  data-testid="featured-news"
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {featured.image && (
                      <div className="aspect-video md:aspect-auto overflow-hidden">
                        <img src={featured.image} alt={featured.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="text-[#007AFF]" size={20} />
                        <span className="text-[#007AFF] font-semibold text-sm uppercase">Featured</span>
                      </div>
                      <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-white mb-4 group-hover:text-[#007AFF] transition-colors">
                        {featured.title}
                      </h2>
                      <p className="text-gray-400 mb-4">{featured.summary}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{featured.author_name}</span>
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(featured.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Category Filters */}
              <div className="flex flex-wrap gap-2 mb-8">
                <Button
                  onClick={() => setSelectedCategory(null)}
                  variant={selectedCategory === null ? 'default' : 'outline'}
                  className={selectedCategory === null ? 'bg-[#007AFF] text-white hover:bg-[#2E90FA]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm" data-testid="category-all">
                  All
                </Button>
                {categories.map((category) => (
                  <Button key={category}
                    onClick={() => setSelectedCategory(category)}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    className={selectedCategory === category ? 'bg-[#007AFF] text-white hover:bg-[#2E90FA]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                    size="sm" data-testid={`category-${category.toLowerCase()}`}>
                    {category}
                  </Button>
                ))}
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#007AFF]"></div>
                </div>
              ) : filteredNews.length === 0 ? (
                <div className="text-center py-12">
                  <Newspaper className="mx-auto text-gray-600 mb-4" size={48} />
                  <p className="text-gray-400 text-lg" data-testid="no-news-message">No news found. Check back soon!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredNews.map((item) => (
                    <Link key={item.news_id} to={`/news/${item.news_id}`}
                      className="bg-[#111111] border border-white/10 rounded-lg overflow-hidden hover:border-[#007AFF]/50 hover:shadow-lg transition-all duration-300 group hover-lift"
                      data-testid={`news-${item.news_id}`}>
                      {item.image && (
                        <div className="aspect-video w-full overflow-hidden">
                          <img src={item.image} alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30 text-xs px-2 py-1 rounded-full">
                            {item.category}
                          </span>
                          {item.tags?.slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">{tag}</span>
                          ))}
                        </div>
                        <h3 className="font-manrope font-semibold text-white text-lg mb-2 group-hover:text-[#007AFF] transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.summary}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>By {item.author_name}</span>
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(item.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NewsPage;
